import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface AuthUser {
  id: string;
  email: string;
  name: string;
  picture: string | null;
  role: string;
  vote_count: number;
  vote_streak: number;
  created_at: string;
}

interface AuthContextType {
  user: AuthUser | null;
  authToken: string | null;
  loading: boolean;
  isAdmin: boolean;
  login: (token: string, userData: AuthUser) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

async function fetchWithAuth<T>(endpoint: string, token: string): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  // Initialize auth state on mount
  useEffect(() => {
    const init = async () => {
      try {
        // Check URL for OAuth callback token
        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token');

        if (urlToken) {
          // OAuth callback: store token, fetch user data, clean URL
          localStorage.setItem('arena_token', urlToken);
          setAuthToken(urlToken);

          try {
            const userData = await fetchWithAuth<AuthUser>('/auth/me', urlToken);
            localStorage.setItem('arena_user', JSON.stringify(userData));
            setUser(userData);
          } catch (err) {
            console.error('Failed to fetch user after OAuth callback:', err);
            localStorage.removeItem('arena_token');
            setAuthToken(null);
          }

          // Clean URL params without triggering navigation
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, document.title, cleanUrl);
        } else {
          // Check localStorage for existing session
          const storedToken = localStorage.getItem('arena_token');
          const storedUser = localStorage.getItem('arena_user');

          if (storedToken) {
            setAuthToken(storedToken);

            if (storedUser) {
              try {
                setUser(JSON.parse(storedUser));
              } catch {
                console.error('Failed to parse stored user data');
              }
            }
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const login = useCallback((token: string, userData: AuthUser) => {
    localStorage.setItem('arena_token', token);
    localStorage.setItem('arena_user', JSON.stringify(userData));
    setAuthToken(token);
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    // Attempt to notify server
    if (authToken) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        });
      } catch {
        // Server logout is best-effort
      }
    }

    localStorage.removeItem('arena_token');
    localStorage.removeItem('arena_user');
    setAuthToken(null);
    setUser(null);
  }, [authToken]);

  const refreshUser = useCallback(async () => {
    if (!authToken) return;

    try {
      const userData = await fetchWithAuth<AuthUser>('/auth/me', authToken);
      localStorage.setItem('arena_user', JSON.stringify(userData));
      setUser(userData);
    } catch (err) {
      console.error('Failed to refresh user data:', err);
    }
  }, [authToken]);

  return (
    <AuthContext.Provider
      value={{ user, authToken, loading, isAdmin, login, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
